import type { ApiClient } from './client';

export const profileMethods = {
async getUserProfile(): Promise<any> {
      try {
        const response = await this.makeRequest<any>('/user_profile.php', {
          method: 'GET',
        });

        if (response.success) {
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to fetch user profile');
        }
      } catch (error: any) {
        console.error('Get user profile error:', error);
        throw new Error(error.message || 'Failed to fetch user profile. Please try again.');
      }
    },


    async getPublicUserProfile(username: string): Promise<any> {
      try {
        const response = await this.makeRequest<any>(`/public_profile.php?username=${encodeURIComponent(username)}`);
        return response.data;
      } catch (error: any) {
        console.error('Failed to fetch public user profile:', error);
        throw new Error(error.message || 'Failed to fetch user profile');
      }
    },


    async updateUserProfile(profileData: {
      firstName: string;
      lastName: string;
      country: string;
      stateProvince: string;
      gender: string;
      birthDate: string;
      instagramUsername?: string;
      facebookUrl?: string;
      websiteUrl?: string;
      bio?: string;
    }): Promise<any> {
      try {
        const response = await this.makeRequest<any>('/update_profile.php', {
          method: 'POST',
          body: JSON.stringify({
            first_name: this.sanitizeString(profileData.firstName),
            last_name: this.sanitizeString(profileData.lastName),
            country: profileData.country,
            state_province: this.sanitizeString(profileData.stateProvince),
            gender: profileData.gender,
            birth_date: profileData.birthDate,
            instagram_username: profileData.instagramUsername ? this.sanitizeString(profileData.instagramUsername) : null,
            facebook_url: profileData.facebookUrl ? this.sanitizeString(profileData.facebookUrl) : null,
            website_url: profileData.websiteUrl ? this.sanitizeString(profileData.websiteUrl) : null,
            bio: profileData.bio ? this.sanitizeString(profileData.bio) : null,
          }),
        });

        if (response.success) {
          // Update stored user data with new profile info
          this.updateStoredUserData({
            first_name: profileData.firstName,
            last_name: profileData.lastName,
          });
          return response.data;
        } else {
          throw new Error(response.error || 'Profile update failed');
        }
      } catch (error: any) {
        console.error('Update user profile error:', error);
        throw new Error(error.message || 'Profile update failed. Please try again.');
      }
    },


    async uploadAvatar(file: File, position?: { x: number; y: number; scale: number }): Promise<any> {
      try {
        const formData = new FormData();
        formData.append('avatar', file);

        if (position) {
          formData.append('position', JSON.stringify(position));
        }

        const response = await this.makeRequest<any>('/avatar_upload.php', {
          method: 'POST',
          body: formData,
        });

        if (response.success) {
          // Update stored user data with new avatar info
          this.updateStoredUserData({
            avatar_url: response.data.avatar_url,
            avatar_position: response.data.position
          });
          return response.data;
        } else {
          throw new Error(response.error || 'Avatar upload failed');
        }
      } catch (error: any) {
        console.error('Upload avatar error:', error);
        throw new Error(error.message || 'Avatar upload failed. Please try again.');
      }
    },


    async deleteAvatar(): Promise<void> {
      try {
        const response = await this.makeRequest<any>('/avatar_upload.php', {
          method: 'DELETE',
        });

        if (response.success) {
          // Update stored user data to remove avatar info
          this.updateStoredUserData({
            avatar_url: null,
            avatar_position: null
          });
        } else {
          throw new Error(response.error || 'Avatar deletion failed');
        }
      } catch (error: any) {
        console.error('Delete avatar error:', error);
        throw new Error(error.message || 'Avatar deletion failed. Please try again.');
      }
    },


    async changePassword(passwordData: {
      currentPassword: string;
      newPassword: string;
    }): Promise<any> {
      try {
        const response = await this.makeRequest<any>('/change_password.php', {
          method: 'POST',
          body: JSON.stringify({
            current_password: passwordData.currentPassword,
            new_password: passwordData.newPassword,
          }),
        });

        if (response.success) {
          return response.data;
        } else {
          throw new Error(response.error || 'Password change failed');
        }
      } catch (error: any) {
        console.error('Change password error:', error);
        throw new Error(error.message || 'Password change failed. Please try again.');
      }
    },

    async updateOnboardingState(
      patch: Record<string, unknown>,
      options?: { reset?: boolean }
    ): Promise<{ onboarding_state: Record<string, unknown> }> {
      const response = await this.makeRequest<{ onboarding_state: Record<string, unknown> }>(
        '/onboarding_update.php',
        {
          method: 'POST',
          body: JSON.stringify(
            options?.reset
              ? { reset: true }
              : { onboarding_state: patch }
          ),
        }
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to update onboarding');
      }
      return response.data || { onboarding_state: {} };
    },
} satisfies ThisType<ApiClient>;
